import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IAgent, IAgentDetailForm, IAgentTag } from 'src/app/features/agents/models/agents.models';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { countriesArray } from 'src/app/shared/data/countries';
import { filter, Observable, Subject, takeUntil } from 'rxjs';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { MatChipInputEvent } from '@angular/material/chips';
import { emailValidator, includesValidator } from 'src/app/shared/validators/fields-validators';
import { AppState } from 'src/app/core/app.state';
import { Store } from '@ngrx/store';
import * as agentsActions from '../../../../store/agents.actions';
import { Actions, ofType } from '@ngrx/effects';

declare var google: any;
@Component({
  selector: 'app-agent-informations-edit-dialog',
  templateUrl: './agent-informations-edit-dialog.component.html',
  styleUrls: ['./agent-informations-edit-dialog.component.scss'],
})
export class AgentInformationsEditDialogComponent implements OnInit {
  agent: IAgent | null = null;

  form!: FormGroup;
  countries: any[] = countriesArray;
  filteredCountries!: Observable<string[]>;
  separatorKeysCodes: number[] = [ENTER, COMMA];
  tags: IAgentTag[] = [];

  private autocomplete: any = null;
  private autocompleteListener: any = null;
  private lastCountryCode: string | null = null;

  destroy$ = new Subject<void>();

  constructor(@Inject(MAT_DIALOG_DATA) public data: { agent: IAgent }, private dialogRef: MatDialogRef<AgentInformationsEditDialogComponent>, private store: Store<AppState>, private actions$: Actions) {}

  ngOnInit(): void {
    this.init();

    this.actions$.pipe(
      ofType(agentsActions.UpdateAgentSuccess),
      takeUntil(this.destroy$)
    ).subscribe((action) => {
      this.dialogRef.close();
    });

    // Listen for country changes and re-initialize autocomplete if country changes
    this.form.get('country')?.valueChanges.pipe(
      takeUntil(this.destroy$),
      filter((value: string) => value !== null && value !== undefined)
    ).subscribe(() => {
      this.form.get('address')?.setValue('');
    });
  }

  init() {
    this.agent = this.data.agent;
    this.form = new FormGroup<IAgentDetailForm>({
      first_name: new FormControl(this.agent?.first_name || '', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      last_name: new FormControl(this.agent?.last_name || '', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      email: new FormControl(this.agent?.email || '', {
        nonNullable: true,
        validators: [Validators.required, emailValidator()],
      }),
      phone: new FormControl(this.agent?.phone || '', {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern(/^\+?[0-9]{7,15}$/),
        ],
      }),
      country: new FormControl(this.agent?.country || '', {
        nonNullable: true,
        validators: [Validators.required, includesValidator(this.countries)],
      }),
      address: new FormControl(this.agent?.address || '', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      vat: new FormControl(this.agent?.vat || '', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      is_user: new FormControl(this.agent?.is_user || false, {
        nonNullable: true,
      }),
      tags: new FormControl(this.agent?.tags || [], {
        nonNullable: true,
      }),
    });

    if(this.agent && this.agent.tags && this.agent.tags.length) {
      this.tags = this.agent?.tags;
    }
  }

  _filter(value: string): string[] {
    const filterValue = this._normalizeValue(value);
    return this.countries.filter(country => this._normalizeValue(country).includes(filterValue));
  }

  _normalizeValue(value: string): string {
    return value.toLowerCase().replace(/\s/g, '');
  }

  _generateRandomColor(): string {
    const hue = Math.floor(Math.random() * 360);
    return `hsl(${hue}, 60%, 30%)`;
  }

  addressAutoComplete(): boolean | void {
    const input = document.getElementById('apartment') as HTMLInputElement;

    if (!input) {
      console.error(`Element with id 'apartment' not found`);
      return;
    }

    // Get the selected country from the form, if any
    const countryControl = this.form.get('country');
    const selectedCountry = countryControl?.value;

    // If country is not selected, mark as touched and set required error, do not initialize autocomplete
    if (!selectedCountry || typeof selectedCountry !== 'string' || selectedCountry.trim() === '') {
      if (countryControl) {
        countryControl.markAsTouched();
        countryControl.setErrors({ ...(countryControl.errors || {}), ['required']: true });
      }
      input.blur();
      return;
    } else if (countryControl && countryControl.hasError('required')) {
      // If country is now selected, remove the required error
      const errors = { ...(countryControl.errors || {}) };
      delete errors['required'];
      if (Object.keys(errors).length === 0) {
        countryControl.setErrors(null);
      } else {
        countryControl.setErrors(errors);
      }
    }

    // Try to get the country code (ISO 2-letter) from the countries array
    let countryCode = '';
    const countryObj = this.countries.find(
      (c: { name: string; code: string }) =>
        c.name.toLowerCase() === selectedCountry.toLowerCase()
    );
    if (countryObj) {
      countryCode = countryObj.code;
    }
    if (!countryCode && selectedCountry.length === 2) {
      countryCode = selectedCountry;
    }
    countryCode = countryCode.toLowerCase();

    const center = { lat: 50.064192, lng: -130.605469 };
    let options: any = {
      strictBounds: false,
      componentRestrictions: { country: countryCode },
      bounds: {
        north: center.lat + 0.1,
        south: center.lat - 0.1,
        east: center.lng + 0.1,
        west: center.lng - 0.1,
      },
    };

    // Only initialize once
    if (!this.autocomplete) {
      this.autocomplete = new google.maps.places.Autocomplete(input, options);
      this.lastCountryCode = countryCode;
      this.autocompleteListener = this.autocomplete.addListener('place_changed', () => {
        const place = this.autocomplete.getPlace();
        if (!place) {
          console.error('No place found');
          return;
        }

        const address = place.formatted_address;
        this.form.get('address')?.setValue(address);
      });
    } else if (this.lastCountryCode !== countryCode) {
      // Update restrictions if country changed
      this.autocomplete.setComponentRestrictions({ country: countryCode });
      this.lastCountryCode = countryCode;
    }
  }

  addTag(event: MatChipInputEvent): void {
    const input = (event.value || '').trim();
    if (input && !this.tags.some(tag => tag.name === input)) {
      const newTag = {
        name: input,
        color: this._generateRandomColor()
      };
      this.tags.push(newTag);
      this.form.get('tags')?.setValue([...this.tags]);
    }
    event.chipInput?.clear();
  }

  removeTag(tagToRemove: { name: string }): void {
    this.tags = this.tags.filter(tag => tag.name !== tagToRemove.name);
    this.form.get('tags')?.setValue([...this.tags]);
  }

  updateAgent(agent: Partial<IAgent>) {
    if (!this.agent?.id) return;

    this.store.dispatch(agentsActions.UpdateAgent({
      id: this.agent.id!,
      agent: agent
    }));
  }

  save() {
    const formValues = this.form.getRawValue();
    const changedValues: any = {};
    Object.keys(formValues).forEach(key => {
      if (
        !this.data.agent ||
        (this.data.agent as any)[key] !== formValues[key]
      ) {
        changedValues[key] = formValues[key];
      }
    });

    this.updateAgent(changedValues);
  }

  cancel() {
    this.dialogRef.close();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    // Remove autocomplete listener if exists
    if (this.autocompleteListener) {
      google.maps.event.removeListener(this.autocompleteListener);
      this.autocompleteListener = null;
    }
    this.autocomplete = null;
  }
}
